import React, { ReactNode, useMemo } from 'react';
import { useAppSelector } from 'redux/hooks/reduxHooks';
import { selectSpecialPage, selectRemainingTime } from 'redux/selectors/selectors';
import { Stack, Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { BoxProps } from '@mui/material/Box';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import { Avatar } from 'components/Avatar/Avatar';
import { IChallangeData } from 'shared/types/challenges.interface';
import { IDebateData, ERoundStatuses } from 'shared/types/debates.interface';
import { CardStatus } from 'components/Card/CardStatus/CardStatus';
import { ECardTypes, EAvatarTypes } from 'shared/types/card.interface';
import { CardEventFeature } from 'components/Card/CardEventFeature/CardEventFeature';
import { getTimerFormat } from 'utils/card-helpers';
import { useDebateDateTime } from 'shared/hooks/useDebateDateTime';
import { CustomMuiIcon } from 'assets/images/icons/customMuiIcons';

const iconStyles = {
  color: 'myGrey.grey600',
  fontSize: { lg: 24, md: 20, sm: 20, xs: 20 },
};

type FullCardDescriptionProps = BoxProps & {
  challengeData: IChallangeData;
  debateData: IDebateData | null;
  children: ReactNode;
};

type TCardFeatures = {
  icon: ReactNode;
  title: string;
  subtitle: string;
};

export const FullCardDescription: React.FC<FullCardDescriptionProps> = ({
  challengeData,
  debateData,
  children,
  ...props
}) => {
  const specialPage = useAppSelector(selectSpecialPage);
  const { totalRemainingTime } = useAppSelector(selectRemainingTime);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const eventDateAndTime = useDebateDateTime(challengeData.start_datetime);

  const currentRound = useMemo(() => {
    const current = debateData?.rounds.find((round) => round.status === ERoundStatuses.Active);
    return current
      ? {
          speaker:
            current.owner === challengeData.initiator.id
              ? `${challengeData.initiator.first_name} ${challengeData.initiator.last_name}`
              : `${challengeData.opponent.first_name} ${challengeData.opponent.last_name}`,
          roundNumber: current.speaker_round_number,
        }
      : {
          speaker: `${challengeData.initiator.first_name} ${challengeData.initiator.last_name}`,
          roundNumber: 0,
        };
  }, [debateData?.rounds, challengeData]);

  const totalTimer = useMemo(
    () =>
      `${getTimerFormat(challengeData.debate_settings.debate_duration).min}:${
        getTimerFormat(challengeData.debate_settings.debate_duration).sec
      }`,
    [challengeData.debate_settings.debate_duration],
  );

  const currentTimer = useMemo(() => {
    const current = debateData?.rounds.find((round) => round.status === ERoundStatuses.Active);
    return current
      ? `${getTimerFormat(totalRemainingTime).min}:${
          getTimerFormat(totalRemainingTime).sec
        } / ${totalTimer}`
      : `${totalTimer} / ${totalTimer}`;
  }, [debateData?.rounds, totalRemainingTime, totalTimer]);

  const participants: TCardFeatures[] = useMemo(
    (): TCardFeatures[] =>
      [
        {
          icon: (
            <Avatar
              type={EAvatarTypes.CardFeatures}
              // "?" in case of test user with empty names
              firstName={challengeData.initiator.first_name || '?'}
              lastName={challengeData.initiator.last_name || '?'}
            />
          ),
          title: `${challengeData.initiator.first_name} ${challengeData.initiator.last_name}`,
          subtitle: 'Initiator',
        },
        {
          icon: debateData ? (
            <Avatar
              type={EAvatarTypes.CardFeatures}
              firstName={challengeData.opponent.first_name || '?'}
              lastName={challengeData.opponent.last_name || '?'}
            />
          ) : (
            <CustomMuiIcon
              type="icon-question"
              sx={{
                fontSize: { lg: 24, xs: 20 },
              }}
            />
          ),
          title: debateData
            ? `${challengeData.opponent.first_name} ${challengeData.opponent.last_name}`
            : '[open slot]',
          subtitle: 'Opponent',
        },
      ].filter(Boolean),
    [challengeData.initiator, challengeData.opponent, debateData],
  );

  const cardFeatures: TCardFeatures[] = useMemo(
    (): TCardFeatures[] =>
      [
        ...(isDesktop ? participants : []),
        {
          icon: <DateRangeOutlinedIcon sx={iconStyles} />,
          title: eventDateAndTime.date,
          subtitle: eventDateAndTime.time,
        },
        {
          icon: <CompareArrowsOutlinedIcon sx={iconStyles} />,
          title: 'Rounds per opponent',
          subtitle: `${challengeData.debate_settings.rounds_per_participant} rounds`,
        },
        {
          icon: <TimerOutlinedIcon sx={iconStyles} />,
          title: 'Total duration',
          subtitle: specialPage
            ? currentTimer
            : `${challengeData.debate_settings.debate_duration / 60} min`,
        },
        specialPage &&
          debateData && {
            icon: <RecordVoiceOverOutlinedIcon sx={iconStyles} />,
            title: 'Current round',
            subtitle: `${currentRound.speaker} 
              ${currentRound.roundNumber}/${challengeData.debate_settings.rounds_per_participant}`,
          },
      ].filter(Boolean),
    [
      eventDateAndTime,
      challengeData,
      specialPage,
      currentTimer,
      debateData,
      currentRound,
      isDesktop,
      participants,
    ],
  );

  return (
    <Box {...props}>
      {isDesktop ? (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            columnGap="24px"
          >
            <Stack direction="row" columnGap="24px">
              <CardStatus
                type={debateData ? ECardTypes.Debate : ECardTypes.Challenge}
                cardInfo={challengeData}
                debate={debateData}
                fullCard
              />
              <Stack direction="row" columnGap="24px" rowGap="24px" flexWrap="wrap">
                {cardFeatures.map((el, index) => (
                  <CardEventFeature
                    key={index}
                    icon={el.icon}
                    title={el.title}
                    subtitle={el.subtitle}
                    fullCard
                  />
                ))}
              </Stack>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              columnGap="16px"
              alignItems="center"
            >
              {children}
            </Stack>
          </Stack>
          <Stack>
            <Typography variant="h1" color="secondary.main" mt="24px" mb="16px">
              {challengeData.thesis}
            </Typography>
            <Typography variant="body2" color="secondary.main">
              {challengeData.description}
            </Typography>
          </Stack>
        </>
      ) : (
        <Stack>
          <Stack direction="row" columnGap="24px" rowGap="24px" flexWrap="wrap" mb="40px">
            {participants?.map((el, index) => (
              <CardEventFeature
                key={index}
                icon={el.icon}
                title={el.title}
                subtitle={el.subtitle}
                fullCard
              />
            ))}
          </Stack>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems={{ md: 'center', sm: 'baseline', xs: 'baseline' }}
          >
            <Typography variant="h1" color="secondary.main" mb="16px">
              {challengeData.thesis}
            </Typography>
            <CardStatus
              type={debateData ? ECardTypes.Debate : ECardTypes.Challenge}
              cardInfo={challengeData}
              debate={debateData}
              fullCard
            />
          </Stack>
          <Typography variant="body2" color="secondary.main">
            {challengeData.description}
          </Typography>
          <Stack
            direction="row"
            columnGap="24px"
            rowGap="24px"
            flexWrap="wrap"
            margin={{ md: '20px 0', sm: '20px 0', xs: '20px 0' }}
          >
            {cardFeatures.map((el, index) => (
              <CardEventFeature
                key={index}
                icon={el.icon}
                title={el.title}
                subtitle={el.subtitle}
                fullCard
              />
            ))}
          </Stack>
          <Stack
            direction="row"
            columnGap="16px"
            justifyContent="flex-end"
            alignItems="space-between"
            alignSelf="flex-end"
            sx={{
              width: 'min-content',
              '@media (max-width:500px)': {
                width: '100%',
                '& .MuiButtonBase-root:only-child': {
                  width: '100%',
                },
                '& .MuiButtonBase-root[data-first="true"].button-share': {
                  width: '100%',
                },
              },
            }}
          >
            {children}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
